class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.container = this.closest('#ProductGridContainer') || document;
    this.grid = this.container.querySelector('#product-grid');
    this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
    this.spinner = this.querySelector('[data-infinite-scroll-spinner]');
    this.status = this.querySelector('[data-infinite-scroll-status]');

    this.sectionId = this.dataset.sectionId;
    this.loading = false;
    this.nextUrl = this.getNextUrl();

    if (!this.grid || !this.nextUrl) {
      this.remove();
      return;
    }

    this.hidePagination();

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      },
      { rootMargin: '600px 0px' }
    );

    this.observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  /* The paginate.next link carries data-next-url. Kept as an attribute rather
     than a class selector because this theme's pagination classes are inverted. */
  getNextUrl() {
    const link = this.container.querySelector('[data-next-url]');
    return link ? link.getAttribute('href') : null;
  }

  getPagination() {
    return this.container.querySelector('.wt-pagination-wrapper');
  }

  hidePagination() {
    const pagination = this.getPagination();
    if (pagination) pagination.classList.add('wt-pagination-wrapper--hidden');
  }

  setBusy(isBusy) {
    if (this.spinner) this.spinner.hidden = !isBusy;
    this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }

  stop() {
    if (this.observer) this.observer.disconnect();
    this.remove();
  }

  async load() {
    if (this.loading || !this.nextUrl) return;

    this.loading = true;
    this.observer.disconnect();
    this.setBusy(true);

    try {
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incomingGrid = doc.getElementById('product-grid');
      if (!incomingGrid) throw new Error('No product grid in response');

      const fragment = document.createDocumentFragment();
      const added = [];

      Array.from(incomingGrid.children).forEach((child) => {
        /* Scroll-reveal animations start at opacity 0 and are initialised on page
           load only. Clearing them here keeps appended products from staying
           invisible. */
        child.classList.remove('scroll-trigger', 'animate--slide-in');
        child.removeAttribute('data-cascade');
        added.push(child);
        fragment.appendChild(child);
      });

      this.grid.appendChild(fragment);

      const currentPagination = this.getPagination();
      const incomingPagination = doc.querySelector('.wt-pagination-wrapper');

      if (currentPagination && incomingPagination) {
        incomingPagination.classList.add('wt-pagination-wrapper--hidden');
        currentPagination.replaceWith(incomingPagination);
      } else if (currentPagination) {
        currentPagination.remove();
      }

      this.nextUrl = this.getNextUrl();
      this.announce(`${added.length} more products loaded.`);
    } catch (error) {
      console.error('Infinite scroll:', error);
      /* Restore the real pagination so the collection stays navigable. */
      const pagination = this.getPagination();
      if (pagination) pagination.classList.remove('wt-pagination-wrapper--hidden');
      this.announce('Could not load more products.');
      this.nextUrl = null;
    } finally {
      this.loading = false;
      this.setBusy(false);

      if (this.isConnected) {
        if (this.nextUrl) {
          this.observer.observe(this.sentinel);
        } else {
          this.stop();
        }
      }
    }
  }
}

customElements.define('infinite-scroll', InfiniteScroll);